import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { VERSION_CONDITIONS, URL_CONDITIONS, URL_CONFIDENTIALITE } from '../constants/legal';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import apiService from '../services/api';
import Logo from '../components/Logo';
import Logger from '../utils/logger';
import { SuccessMessages, ErrorMessages, getApiErrorMessage } from '../constants/messages';
import { Button, Input } from '../components/ui';

interface RegisterForm {
  email: string;
  password: string;
  confirmPassword: string;
  nom: string;
  prenom: string;
  conditions: boolean;
}

const RegisterPage: React.FC = () => {
  const { t } = (useTranslation as any)('auth');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { register, handleSubmit, watch, formState: { errors } } = useForm<RegisterForm>();

  const password = watch('password');

  const onSubmit = async (data: RegisterForm) => {
    if (data.password !== data.confirmPassword) {
      toast.error(ErrorMessages.VALIDATION_ERROR);
      return;
    }

    setLoading(true);
    try {
      Logger.log('[RegisterPage] Envoi de la requete d\'inscription...');
      await apiService.register({
        nom: data.nom,
        prenom: data.prenom,
        email: data.email,
        mot_de_passe: data.password,
        conditions_version: VERSION_CONDITIONS
      });

      Logger.log('[RegisterPage] Inscription reussie');
      toast.success(SuccessMessages.REGISTRATION_SUCCESS);

      // Rediriger vers la page de verification avec l'email via state (pas dans l'URL)
      navigate('/verify-account', { state: { email: data.email } });
      Logger.log('[RegisterPage] Navigation declenchee');
    } catch (error: unknown) {
      Logger.error('Registration failed', error);
      toast.error(getApiErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-surface-0 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center">
          <Logo size="large" />
        </div>
        <h2 className="mt-6 text-center text-3xl font-bold text-text-primary">
          {t('register.title')}
        </h2>
        <p className="mt-2 text-center text-sm text-text-secondary">
          {t('or', { ns: 'common' })}{' '}
          <Link
            to="/login"
            className="font-medium text-accent-400 hover:text-accent-300"
          >
            {t('register.loginLink')}
          </Link>
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-surface-2 py-8 px-4 shadow-card sm:rounded-card sm:px-10">
          <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
            {/* Nom */}
            <Input
              {...register('nom', {
                required: t('name.required', { ns: 'validation' })
              })}
              id="nom"
              type="text"
              label={t('register.lastName')}
              autoComplete="family-name"
              aria-required="true"
              placeholder={t('register.lastNamePlaceholder')}
              error={errors.nom?.message}
            />

            {/* Prenom */}
            <Input
              {...register('prenom', {
                required: t('firstName.required', { ns: 'validation' })
              })}
              id="prenom"
              type="text"
              label={t('register.firstName')}
              autoComplete="given-name"
              aria-required="true"
              placeholder={t('register.firstNamePlaceholder')}
              error={errors.prenom?.message}
            />

            {/* Email */}
            <Input
              {...register('email', {
                required: t('email.required', { ns: 'validation' }),
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: t('email.invalid', { ns: 'validation' })
                }
              })}
              id="email"
              type="email"
              label={t('register.email')}
              autoComplete="email"
              aria-required="true"
              placeholder={t('register.emailPlaceholder')}
              error={errors.email?.message}
            />

            {/* Password */}
            <Input
              {...register('password', {
                required: t('password.required', { ns: 'validation' }),
                minLength: {
                  value: 8,
                  message: t('password.minLength', { ns: 'validation', count: 8 })
                },
                pattern: {
                  value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
                  message: t('password.pattern', { ns: 'validation' })
                }
              })}
              id="password"
              type="password"
              label={t('register.password')}
              autoComplete="new-password"
              aria-required="true"
              placeholder="••••••••"
              error={errors.password?.message}
            />

            {/* Confirm Password */}
            <Input
              {...register('confirmPassword', {
                required: t('password.confirmRequired', { ns: 'validation' }),
                validate: (value) => value === password || t('password.mismatch', { ns: 'validation' })
              })}
              id="confirmPassword"
              type="password"
              label={t('register.confirmPassword')}
              autoComplete="new-password"
              aria-required="true"
              placeholder="••••••••"
              error={errors.confirmPassword?.message}
            />

            {/* Submit Button */}
            <div>
              <Button
                type="submit"
                variant="primary"
                size="md"
                loading={loading}
                disabled={loading}
                className="w-full"
              >
                {loading ? t('register.submitting') : t('register.submit')}
              </Button>
            </div>

            {/* Acceptation des conditions. CASE A COCHER, et non une simple mention :
                l'ecran affirmait « En creant un compte, vous acceptez... » avec deux
                liens `href="#"` qui ne menaient nulle part -- il faisait accepter deux
                documents dont l'un etait introuvable et l'autre inexistant. */}
            <div>
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  className="mt-0.5 h-4 w-4 rounded border-border bg-surface text-accent-500 focus:ring-accent-500"
                  {...register('conditions', {
                    required: t('register.terms.required') as string
                  })}
                />
                <span className="text-xs text-text-secondary">
                  {t('register.terms.text')}{' '}
                  <a
                    href={URL_CONDITIONS}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-medium text-accent-400 hover:text-accent-300"
                  >
                    {t('register.terms.termsOfService')}
                  </a>{' '}
                  <span className="text-text-tertiary">
                    ({t('register.terms.version', { version: VERSION_CONDITIONS })})
                  </span>{' '}
                  {t('register.terms.and')}{' '}
                  <a
                    href={URL_CONFIDENTIALITE}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-medium text-accent-400 hover:text-accent-300"
                  >
                    {t('register.terms.privacyPolicy')}
                  </a>
                </span>
              </label>
              {errors.conditions && (
                <p className="mt-2 text-sm text-red-400">{errors.conditions.message}</p>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
